<template>
  <div class="d-flex align-items-center">
    <b-button
      v-if="showButtons"
      :disabled="adjustStock <= min || disabled"
      variant="outline-primary"
      size="sm"
      class="d-flex align-items-center justify-content-center"
      style="width: 38px; height: 38px; min-width: 38px; min-height: 38px"
      @click="onMinus"
    >
      <i class="fas fa-minus"></i>
    </b-button>
    <div
      class="input-wrapper"
      :class="{ 'input-group': prefix || suffix }"
    >
      <div v-if="prefix" class="input-group-prepend">
        <span
          class="input-group-text addon-text"
          :class="{ 'disabled': disabled }"
        >{{ prefix }}</span>
      </div>
      <div class="input-field-wrapper" :style="prefix || suffix ? 'flex: 1; min-width: 0;' : 'width: 100%;'">
        <b-form-input
          inputmode="numeric"
          style="width: 100%"
          :value="value"
          :placeholder="placeholder"
          :max="max"
          :min="min"
          @input="onInputChange"
          @blur="onBlur($event)"
          @paste="onPaste($event)"
          :disabled="disabled"
          :class="{
            'no-arrow': true,
            'text-left': textAlign === 'left',
            'text-center': textAlign === 'center',
            'text-right': textAlign === 'right',
            'is-invalid': invalid,
            'required': isRequired,
            'rounded-right-0': !!suffix,
            'rounded-left-0': !!prefix,
          }"
          @keydown="allowOnlyTwoDecimalNumber($event)"
        ></b-form-input>
        <svg
          v-if="invalid && showErrorIcon"
          class="input-invalid-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#dc3545"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="13" />
          <circle cx="12" cy="16.5" r="0.5" fill="#dc3545" stroke="none" />
        </svg>
      </div>
      <div v-if="suffix" class="input-group-append">
        <span
          class="input-group-text addon-text"
          :class="{ 'disabled': disabled }"
        >{{ suffix }}</span>
      </div>
    </div>
    <b-button
      v-if="showButtons"
      :disabled="adjustStock >= max || disabled"
      variant="outline-primary"
      size="sm"
      class="d-flex align-items-center justify-content-center"
      style="width: 38px; height: 38px; min-width: 38px; min-height: 38px"
      @click="onPlus"
    >
      <i class="fas fa-plus"></i>
    </b-button>
  </div>
</template>

<script>
import "@/theme/modal/ProductAndStock/Product.scss";
import BigNumber from "bignumber.js";
import { validateTwoDecimal } from "@/utils/numberValidation";

export default {
  name: "InputNumberField",
  components: {},
  props: {
    adjustStock: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 9999999999.99,
    },
    min: {
      type: Number,
      default: -9999999999.99,
    },
    onChange: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    showButtons: {
      type: Boolean,
      default: true,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: "",
    },
    textAlign: {
      type: String,
      default: "center",
      validator: value => ["left", "center", "right"].includes(value),
    },
    allowDecimal: {
      type: Boolean,
      default: true,
    },
    prefix: {
      type: String,
      default: "",
    },
    suffix: {
      type: String,
      default: "",
    },
    showErrorIcon: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoading: false,
      value: this.adjustStock == null ? "" : new BigNumber(this.adjustStock).toNumber(),
    };
  },
  validations: {},
  computed: {},
  methods: {
    onInputChange(value) {
      this.value = value;
      const raw = (value ?? "").toString().trim();
      if (raw === "") return;

      const bn = new BigNumber(raw);
      if (!bn.isFinite()) return;

      this.onChange(bn.toFixed(2));
    },
    onMinus() {
      if (this.value > this.min) {
        const result = new BigNumber(this.value).minus(1);
        if (result.isLessThan(this.min)) {
          return this.onChange(new BigNumber(this.min).toFixed(2));
        }
        if (!validateTwoDecimal(result)) {
          this.onChange(result.toFixed(2));
        } else {
          this.onChange(result.toNumber());
        }
      }
    },
    onPlus() {
      if (this.value < this.max) {
        const result = new BigNumber(this.value).plus(1);
        if (result.isGreaterThan(this.max)) {
          return this.onChange(new BigNumber(this.max).toFixed(2));
        }
        if (!validateTwoDecimal(result)) {
          this.onChange(result.toFixed(2));
        } else {
          this.onChange(result.toNumber());
        }
      }
    },
    onBlur() {
      let raw = (this.value ?? "").toString().trim();

      if (raw === "") {
        this.value = "";
        this.onChange(null);
        return;
      }

      //  -0, -0.0, 0.0, 00.00, 000, 000.000
      const isZeroLike = /^-?0*(?:\.0*)?$/.test(raw);
      if (isZeroLike) {
        this.value = "0";
        this.onChange(new BigNumber(0));
        return;
      }

      const bn = new BigNumber(raw);
      if (!bn.isFinite()) {
        this.value = "";
        this.onChange(null);
        return;
      }

      if (this.value < this.min) {
        this.value = new BigNumber(this.min).toNumber();
      } else if (this.value > this.max) {
        this.value = new BigNumber(this.max).toNumber();
      } else if (!this.allowDecimal) {
        this.value = new BigNumber(this.value).integerValue(BigNumber.ROUND_DOWN).toNumber();
      } else if (!validateTwoDecimal(this.value)) {
        this.value = new BigNumber(this.value);
      }
      this.onChange(new BigNumber(this.value));
    },
    allowOnlyTwoDecimalNumber(e) {
      if (e.ctrlKey || e.metaKey) {
        const k = (e.key || "").toLowerCase();
        const allowed = ["a", "c", "v", "x", "z", "y"];
        if (allowed.includes(k)) return;
      }
      if (e.shiftKey && e.key === "Insert") return;

      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];

      if (allowedKeys.includes(e.key)) return;

      const input = e.target;
      const value = input.value || "";
      const { selectionStart, selectionEnd } = input;

      const nextValue =
        value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);

      if (!this.isValueWithinRange(nextValue)) {
        e.preventDefault();
      }

      // Minus: only at the very start
      if (e.key === "-") {
        if (selectionStart !== 0) {
          e.preventDefault();
        }
        return;
      }

      // One decimal point only (block if decimal not allowed)
      if (e.key === ".") {
        if (!this.allowDecimal || value.includes(".")) {
          e.preventDefault();
        }
        return;
      }

      // Digits 0–9: allow only if result would have at most 2 decimal places
      if (/^\d$/.test(e.key)) {
        const newValue =
          value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);
        const parts = newValue.split(".");
        if (parts.length === 2 && parts[1].length > 2) {
          e.preventDefault();
        }
        return;
      }
      e.preventDefault();
    },
    onPaste(e) {
      const raw = (
        (e.clipboardData || window.clipboardData)?.getData("text") || ""
      )
        .trim()
        .replace(/,/g, "");

      const pattern = this.allowDecimal ? /^-?\d*(?:\.\d*)?$/ : /^-?\d+$/;
      if (!raw || raw === "-" || raw === "." || !pattern.test(raw)) {
        e.preventDefault();
        return;
      }

      e.preventDefault();

      let bn = new BigNumber(raw);
      if (!bn.isFinite()) return;

      const minBN = new BigNumber(this.min ?? 0);
      const maxBN = new BigNumber(this.max ?? 0);

      if (bn.isLessThan(minBN) || bn.isGreaterThan(maxBN)) {
        e.preventDefault();
        return;
      }

      if (bn.isLessThan(minBN)) bn = minBN;
      if (bn.isGreaterThan(maxBN)) bn = maxBN;

      bn = this.allowDecimal
        ? bn.decimalPlaces(2, BigNumber.ROUND_DOWN)
        : bn.integerValue(BigNumber.ROUND_DOWN);

      this.value = bn.isZero() ? "0" : bn.toString();
      this.onChange(bn);
    },
    isValueWithinRange(rawValue) {
      if (
        rawValue === "" ||
        rawValue === "-" ||
        rawValue === "." ||
        rawValue === "-."
      ) {
        return true;
      }

      if (!/^-?\d*(?:\.\d*)?$/.test(rawValue)) {
        return false;
      }

      const bn = new BigNumber(rawValue);
      if (!bn.isFinite()) return false;

      const minBN = new BigNumber(this.min);
      const maxBN = new BigNumber(this.max);

      return bn.isGreaterThanOrEqualTo(minBN) && bn.isLessThanOrEqualTo(maxBN);
    },
  },
  watch: {
    adjustStock(newVal) {
      if (newVal == null) {
        this.value = "";
        return;
      }
      const result = new BigNumber(newVal);
      if (!validateTwoDecimal(result)) {
        this.value = result.toFixed(2);
      } else {
        this.value = result.toNumber();
      }
    },
  },
};
</script>

<style scoped>
.required {
  background-color: #ccecff6c;
}

.input-wrapper {
  position: relative;
  display: inline-flex;
  width: 100%;
}

.addon-text {
  max-width: 80px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
  display: block;
}

.addon-text.disabled {
  color: #c6c6c6;
}


.input-field-wrapper {
  position: relative;
}

.input-invalid-icon {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
  width: 20px;
  height: 20px;
}

::v-deep .rounded-right-0 {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

::v-deep .rounded-left-0 {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

.no-arrow::-webkit-inner-spin-button,
.no-arrow::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-arrow {
  -moz-appearance: textfield;
}

button:disabled {
  background-color: white;
  border-color: #ebebeb;
  color: #ebebeb;
  cursor: not-allowed;
}

::v-deep .form-control.is-invalid,
::v-deep .was-validated .form-control:invalid {
  background-image: none !important;
  padding-right: 0.75rem !important;
}

::v-deep .form-control:disabled {
  background-color: #e9ecef !important;
  color: #6c757d !important;
}

::v-deep .form-control:disabled::placeholder {
  color: #6c757d !important;
}
</style>
